import { ApiError } from '../interceptors/axiosConfig';
import showToast from './toastUtils';

// Enhanced error handling utility for services
export class ErrorHandler {
  /**
   * Handle API errors with appropriate toast notifications
   * @param error - The error object
   * @param context - Context for better error messages
   * @param showToast - Whether to show toast (default: true)
   * @returns Formatted error message
   */
  static handleApiError(
    error: any, 
    context: string = 'Operation', 
    showToastNotification: boolean = true
  ): string {
    let message = '';
    
    if (error instanceof ApiError) {
      message = error.message;
    } else if (error.response) {
      const errorData = error.response.data || {};
      message = errorData.error || errorData.detail || errorData.message || `Request failed with status ${error.response.status}`;
    } else if (error.request) {
      message = 'Network error: Unable to connect to the server. Please check your internet connection.';
    } else {
      message = error.message || 'An unexpected error occurred';
    }
    
    // Add context to the message
    const contextualMessage = `${context}: ${message}`;
    
    // Show toast if requested
    if (showToastNotification) {
      const status = error.response?.status || error.status || 0;

      // Suppress not-found toasts; many 404s are expected/benign in UI flows.
      if (status === 404) {
        return contextualMessage;
      }
      
      // Use warning for client errors (4xx), error for server errors (5xx) and network errors
      if (status >= 400 && status < 500) {
        showToast.warning(contextualMessage);
      } else {
        showToast.error(contextualMessage);
      }
    }
    
    return contextualMessage;
  }
  
  /**
   * Handle service-specific errors with custom messages
   * @param error - The error object
   * @param operation - The operation being performed
   * @param entity - The entity being operated on (e.g., 'game', 'audit', 'store')
   * @param action - The action being performed (e.g., 'create', 'update', 'delete', 'fetch')
   * @returns Formatted error message
   */
  static handleServiceError(
    error: any,
    operation: string,
    entity: string,
    action: 'create' | 'update' | 'delete' | 'fetch' | 'upload' | 'process' = 'fetch'
  ): string {
    const actionMessages = {
      create: `Failed to create ${entity}`,
      update: `Failed to update ${entity}`,
      delete: `Failed to delete ${entity}`,
      fetch: `Failed to load ${entity} data`,
      upload: `Failed to upload ${entity}`,
      process: `Failed to process ${entity}`
    };
    
    const baseMessage = actionMessages[action] || `Failed to ${action} ${entity}`;
    return this.handleApiError(error, baseMessage, true);
  }
  
  /**
   * Handle errors with custom success/error callbacks
   * @param promise - The promise to handle
   * @param successMessage - Message to show on success
   * @param errorContext - Context for error messages
   * @param showSuccessToast - Whether to show success toast
   * @returns Promise that resolves to the result or rejects with error
   */
  static async handlePromise<T>(
    promise: Promise<T>,
    successMessage?: string,
    errorContext: string = 'Operation',
    showSuccessToast: boolean = false
  ): Promise<T> {
    try {
      const result = await promise;
      
      if (showSuccessToast && successMessage) {
        showToast.success(successMessage);
      }
      
      return result;
    } catch (error) {
      this.handleApiError(error, errorContext, true);
      throw error;
    }
  }
}

// Convenience functions for common operations
export const handleGameError = (error: any, action: 'create' | 'update' | 'delete' | 'fetch' = 'fetch') =>
  ErrorHandler.handleServiceError(error, 'Game management', 'game', action);

export const handleAuditError = (error: any, action: 'create' | 'update' | 'delete' | 'fetch' | 'upload' | 'process' = 'fetch') =>
  ErrorHandler.handleServiceError(error, 'Audit management', 'audit', action);

export const handleStoreError = (error: any, action: 'create' | 'update' | 'delete' | 'fetch' = 'fetch') =>
  ErrorHandler.handleServiceError(error, 'Store management', 'store', action);

export const handleAuthError = (error: any, action: 'login' | 'logout' | 'refresh' = 'login') =>
  ErrorHandler.handleApiError(error, `Authentication ${action}`, true);

export const handleUploadError = (error: any, fileType: string = 'file') =>
  ErrorHandler.handleServiceError(error, 'File upload', fileType, 'upload');

export default ErrorHandler;
